import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { WelletContext } from '../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setCanRefresh } from '../../actions/appActions';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import Button from '../../components/Common/Button/Button';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import InputFormBorder from '../../components/Common/InputFormBorder/InputFormBorder';
import countries from '../../../src/data/countrys.json';
import GeneralSelectorPicker from '../../components/SelectorPicker/GeneralSelectorPicker';
import IndustryRoleSelector from '../../components/Common/IndustryRoleSlector/IndustryRoleSelector';
import { fetchStates } from '../../services/geo/geoService';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import { getQueryString } from '../../helpers/url';
import ErrorMapperRegisterLogin from '../../helpers/ErrorMapperRegisterLogin';

const SignUpPersonalInformation = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const welletContext = useContext(WelletContext);
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const language = useSelector((state) => state.app.language);
  const tenantId = useSelector((state) => state.app.tenantId);
  const currentMarket = useSelector((state) => state.app.currentMarket);
  const [errorMessage, setErrorMessage] = useState(null);
  const phone = getQueryString('phone');
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const countryCode = searchParams.get('countryCode');
  const [loadingStates, setLoadingStates] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState(t('signUp.selectSector'));
  const [selectedRp, setSelectedRp] = useState({ label: t('signUp.selectRp') });
  const [selectedCity, setSelectedCity] = useState({ label: t('signUp.selectCity') });
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [states, setStates] = useState([]);
  const [acceptConditions, setAcceptConditions] = useState(false);

  function validate(values) {
    let errors = {};
    if (!values.firstName) {
      errors.firstName = t('nameError.EMPTY_NAME');
    }

    if (!values.lastName) {
      errors.lastName = t('nameError.EMPTY_LASTNAME');
    }

    if (!values.email) {
      errors.email = t('emailError.EMPTY_EMAIL');
    } else if (!values.email.includes('@')) {
      errors.email = t('emailError.EMAIL_@');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    if (values.email && !values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMPTY_EMAIL');
    } else if (values.email !== values.emailConfirmation) {
      errors.emailConfirmation = t('emailError.EMAIL_NOT_MATCH');
    }

    if (!selectedCountry) {
      errors.country = t('selectCountry');
    }

    if (states.length > 0 && !selectedState) {
      errors.state = t('state.requieredText');
    }

    if (!values.city || (values.city && values.city.length === 0)) {
      errors.city = t('city.requieredText');
    }

    if (!values.postalCode) {
      errors.postalCode = t('postalCode.requieredText');
    }

    if (!values.address) {
      errors.address = t('address.requieredText');
    }

    if (!values.activityId) {
      errors.activityId = t('errorSignUp.NO_INDUSTRY');
    }

    if (!values.role) {
      errors.role = t('errorSignUp.NO_ROLE');
    }

    if (!values.company) {
      errors.company = t('errorSignUp.NO_COMPANY');
    }

    if (!acceptConditions) {
      errors.acceptConditions = t('checkInformationComplete');
    }

    if (!values.rpId && currentMarket?.requiresRP) {
      errors.rpId = t('errorSignUp.RP');
    }

    if (values.rpId === '0') {
      if (!values.cityId) {
        errors.cityId = t('errorSignUp.CITY');
      }
    }

    return errors;
  }

  const sendForm = async () => {
    try {
      const result = await welletContext.apis.tickets.post(`users/register/available`, {
        email: values.email,
        tenantId: tenantId,
      });

      if (result.data.isAvailable) {
        const params = new URLSearchParams(values);
        params.append('userCountryCode', selectedCountry);
        if (selectedState) {  
          params.append('stateId', selectedState);
        }
        params.append('industryId', selectedIndustry.value);
        params.append('phone', phone);
        params.append('token', token);
        params.append('countryCode', countryCode);

        history.push({
          pathname: '/sign-up-create-user',
          search: `?` + params.toString(),
        });
      } else {
        setErrorMessage(<ErrorMapperRegisterLogin errorCode={result.data.availableMessage} />);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage(t('callApiError'));
    }
  };

  const handleChangeCountry = async (selectedOption) => {
    setSelectedState(null);
    setStates([]);
    setSelectedCountry(selectedOption.value);
    await getStates(selectedOption.value);
    setSelectedState(null);
  };

  const handleChangeState = (selectedOption) => {
    handleChange({
      target: { name: 'state', value: selectedOption.value },
      persist: () => {},
    });
    setSelectedState(selectedOption.value);
  };

  const getStates = async (countryCode) => {
    try {
      setLoadingStates(true);
      const response = await fetchStates(countryCode, welletContext);
      if (response && response.data) {
        setStates(response.data);
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const handleAcceptConditions = () => {
    setAcceptConditions(!acceptConditions);
  };

  const { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  useEffect(() => {
    dispatch(setCanRefresh(false));

    if (email) {
      handleChange({
        target: { name: 'email', value: email },
        persist: () => {},
      });

      handleChange({
        target: { name: 'emailConfirmation', value: email },
        persist: () => {},
      });
    }

    return () => {
      dispatch(setCanRefresh(true));
    };
  }, []);

  return (
    <>
      <Header />
      <ContentApp background={true}>
        <div className=''>
          <div className='font-medium bold text-white' style={{ lineHeight: '1.6rem' }}>
            {t('signUpPromoter.title')}
          </div>
          <div className='mt-1 text-secondary subtitle' style={{ lineHeight: '1.3rem' }}>
            {t('signUpPromoter.description')}
          </div>
          <div className='fw-semibold text-primary font-tiny mt-4 pl-1'>
            {t('personalInformation')}
          </div>
          <form onSubmit={handleSubmit} className='mt-3'>
            <div className='row'>
              <div className='col pr-1'>
                <InputFormBorder
                  name='firstName'
                  type='text'
                  value={values.firstName}
                  error={errors.firstName}
                  placeholder={t('profile.firstName')}
                  label={t('profile.firstName')}
                  onChange={handleChange}
                  customClass='mt-2'
                />
              </div>
              <div className='col pl-1'>
                <InputFormBorder
                  name='lastName'
                  type='text'
                  customClass='mt-2'
                  value={values.lastName}
                  error={errors.lastName}
                  placeholder={t('profile.lastName')}
                  label={t('profile.lastName')}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div>
              <InputFormBorder
                color='#757575'
                name='email'
                type='email'
                icon={'mail'}
                onChange={handleChange}
                value={email ? email : values.email}
                error={errors.email}
                placeholder={'E-mail'}
                forceLowecase={true}
                disabled={email ? true : false}
              />
            </div>
            <div>
              <InputFormBorder
                color='#757575'
                name='emailConfirmation'
                type='email'
                icon={'mail'}
                onChange={handleChange}
                value={email ? email : values.emailConfirmation}
                error={errors.emailConfirmation}
                placeholder={t('signUp.confirmEmail')}
                forceLowecase={true}
                disabled={email ? true : false}
              />
            </div>
            <div className='fw-semibold text-primary font-tiny mt-4 pl-1'>
              {t('informationResidence')}
            </div>
            <div className='mt-3 fade-up position-relative'>
              <GeneralSelectorPicker
                options={countries.map(({ name, countryCode }) => ({
                  label: name,
                  value: countryCode,
                }))}
                selectedValue={selectedCountry}
                onChange={handleChangeCountry}
                placeholder={t('stripe.countryOfResidence')}
              />
              {errors.country && (
                <div
                  className='error-label text-center'
                  style={{ position: 'relative', marginTop: '10px' }}
                >
                  {errors.country}
                </div>
              )}
            </div>
            <div className='mt-25 position-relative' key={`state-selector-${selectedCountry}`}>
              <GeneralSelectorPicker
                options={states.map(({ name, id }) => ({
                  label: name,
                  value: id,
                }))}
                selectedValue={selectedState}
                onChange={handleChangeState}
                placeholder={t('state.label')}
                disabled={states.length === 0}
              />
              {errors.state && (
                <div
                  className='error-label text-center'
                  style={{ position: 'relative', marginTop: '10px' }}
                >
                  {errors.state}
                </div>
              )}
            </div>
            <div className='row mt-25'>
              <div className='col-8 pr-1'>
                <InputFormBorder
                  color='#757575'
                  name='city'
                  type='text'
                  onChange={handleChange}
                  value={values.city}
                  error={errors.city}
                  placeholder={t('city.label')}
                />
              </div>
              <div className='col-4 pl-1'>
                <InputFormBorder
                  color='#757575'
                  name='postalCode'
                  type='text'
                  onChange={handleChange}
                  value={values.postalCode}
                  error={errors.postalCode}
                  placeholder={t('postalCode.label')}
                />
              </div>
            </div>
            <InputFormBorder
              color='#757575'
              name='address'
              type='text'
              onChange={handleChange}
              value={values.address}
              error={errors.address}
              placeholder={t('address.label')}
            />

            <div className='fw-semibold text-primary font-tiny mt-4 pl-1'>
              {t('employmentInformation')}
            </div>
            <div className='mt-3'>
              <IndustryRoleSelector
                textInfo={true}
                handleChange={handleChange}
                selectedIndustry={selectedIndustry}
                setSelectedIndustry={setSelectedIndustry}
                selectedRp={selectedRp}
                setSelectedRp={setSelectedRp}
                selectedCity={selectedCity}
                setSelectedCity={setSelectedCity}
                values={values}
                errors={errors}
                requiresRP={currentMarket.requiresRP}
                lang={language}
              />
            </div>

            <div className='fw-semibold text-primary font-tiny mt-4 pl-1'>
              {t('consentVerification')}
            </div>
            <div className='mt-4'>
              <label className='checkbox-container'>
                <input
                  type='checkbox'
                  style={{ width: '20px', height: '20px' }}
                  checked={acceptConditions}
                  onChange={handleAcceptConditions}
                />
                <span className='checkmark'></span>
                <div style={{ marginLeft: '2rem' }}>{t('informationComplete')}</div>
              </label>
              {errors.acceptConditions && (
                <div
                  className='error-label text-center'
                  style={{ position: 'relative', marginTop: '10px' }}
                >
                  {errors.acceptConditions}
                </div>
              )}
            </div>
            {errorMessage && (
              <div className='mt-3'>
                <ErrorBox title={errorMessage} />
              </div>
            )}
            <Button
              className='btn-primary mt-5'
              size='large'
              fontSize={'18px'}
              text={t('continue')}
              typeIcon='arrow'
              iconColor='#000'
            />
          </form>
        </div>
      </ContentApp>
    </>
  );
};

export default SignUpPersonalInformation;
